import { Coordinate, DeviceStatusInfo, ReverseGeocodeAddress } from "@fairfleet/geotab";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef } from "react";
import { useDataGridSettings } from "@/components/DataGridSettingsProvider";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { chunkArray } from "@/utilities/chunkArray";

/**
 * How many coordinates go into one `GetAddresses` call. Reverse geocoding is among the slowest
 * calls Geotab offers, and a report over a fleet-sized period asks about thousands of positions
 * at once — as a single request that is one long round trip that either arrives whole or not at
 * all. Split up, the parts are queued into the same `ExecuteMultiCall` batches as everything else.
 */
export const COORDINATES_PER_CALL = 500;

/**
 * Fetches the {@link ReverseGeocodeAddress}(s) for the given coordinates.
 *
 * @param coordinates - The coordinates to fetch the addresses for.
 * @param hosAddresses - When `true` the result will include the direction and distance to the
 * nearest city with a population greater than 5000.
 * @param movingAddresses - When `true` the coordinates will be interpreted as a moving object.
 * @returns The addresses, one per input coordinate and in the same order.
 */
export function useAddresses(
  coordinates: Coordinate[] = [],
  hosAddresses = false,
  movingAddresses = false,
) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  return useQuery({
    queryKey: ["addresses", activeSessionId, coordinates, hosAddresses, movingAddresses],

    queryFn: async ({ signal }) => {
      if (coordinates.length === 0) {
        return [];
      }

      // Order is what pairs an address back to its coordinate, so the chunks are awaited
      // together and flattened in order rather than merged as they arrive.
      const chunks = await Promise.all(
        chunkArray(coordinates, COORDINATES_PER_CALL).map(chunk =>
          geotabClient.getAddresses(chunk, hosAddresses, movingAddresses, signal),
        ),
      );
      return chunks.flat();
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * Fetches the addresses for the given device status information.
 *
 * @param deviceStatusInfos - The array of device status info objects.
 * @param hosAddresses - When `true`, the result will include the direction and distance to the nearest city with a population greater than 5000.
 * @param movingAddresses - When `true`, the coordinates will be interpreted as a moving object.
 * @returns - A dictionary with the device ID as the key and the address as the value.
 */
export function useAddressesByDeviceStatusInfo(
  deviceStatusInfos: Record<string, DeviceStatusInfo>,
  hosAddresses = false,
  movingAddresses = false,
) {
  const queryClient = useQueryClient();
  const cacheRef = useRef<Record<string, ReverseGeocodeAddress>>({});

  const { geotabClient, activeSessionId } = useGeotabClient();
  const { loadAddresses } = useDataGridSettings();
  const coordinates = useMemo(() => {
    return Object.values(deviceStatusInfos).map(({ latitude, longitude, device }) => ({
      latitude,
      longitude,
      deviceId: device.id,
    }));
  }, [deviceStatusInfos]);

  const cacheKey = ["addresses", activeSessionId, coordinates, hosAddresses, movingAddresses];

  return useQuery<Record<string, ReverseGeocodeAddress>>({
    queryKey: cacheKey,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    enabled: loadAddresses,

    queryFn: async ({ signal }) => {
      const latLongPairsWithDeviceId = coordinates.map(info => ({
        latitude: info.latitude,
        longitude: info.longitude,
        deviceId: info.deviceId,
      }));

      // Separate cached coordinates from the ones that need to be fetched
      const newCoordinates = latLongPairsWithDeviceId.filter(coord => {
        const key = `${coord.latitude}-${coord.longitude}`;
        return !cacheRef.current[key]; // Fetch if not in cache
      });

      let newAddresses: ReverseGeocodeAddress[] = [];

      if (newCoordinates.length > 0) {
        // Fetch the addresses using the API only for new coordinates
        const latLongPairs = newCoordinates.map(coord => ({
          x: coord.longitude,
          y: coord.latitude,
        }));

        //await geotabClient.getAddresses(latLongPairs, hosAddresses, movingAddresses, signal);
        // The GetAddresses call takes pretty long, so in order to not block any other requests make  it a sperate multicall
        const newAddressesmc = await geotabClient.call(
          "ExecuteMultiCall",
          {
            calls: [
              {
                method: "GetAddresses",
                params: { coordinates: latLongPairs, hosAddresses, movingAddresses },
              },
            ],
          },
          signal,
        );

        // @ts-ignore
        newAddresses = newAddressesmc?.[0];
      }

      // Store new addresses in cache
      newCoordinates.forEach((coord, index) => {
        const key = `${coord.latitude}-${coord.longitude}`;
        cacheRef.current[key] = newAddresses[index]; // Cache the new address
      });

      // Create a dictionary of addresses, including both cached and newly fetched addresses
      const addressDict = latLongPairsWithDeviceId.reduce(
        (acc: Record<string, ReverseGeocodeAddress>, coord) => {
          const key = `${coord.latitude}-${coord.longitude}`;
          acc[coord.deviceId] = cacheRef.current[key]; // Retrieve from cache
          return acc;
        },
        {},
      );
      queryClient.setQueryData(cacheKey, addressDict);

      return addressDict;
    },
  });
}
